/**
 * Short labels for the compact knockout bracket box. Each side shows a tight code under its
 * crest: a FIFA tri-code for a resolved nation (GER, BRA, NED), a name-derived abbreviation for
 * a club/league team, or a compact placeholder for a still-undecided slot (e.g. "1A", "QF2").
 *
 * The bracket box is too narrow for full team names, so a short code is required by the layout,
 * not cosmetic. We do NOT try to reproduce a data provider's proprietary seed encoding — these
 * are derived purely from the data we hold (`KnockoutBracketModel`).
 */

import type { BracketTeamSlot } from '../types/knockoutBracket.types'
import { COUNTRY_TRI_CODE } from '../constants/countryTriCode.constants'

/** Feeding-round → compact code used inside "winner advances" placeholders. */
const ROUND_SHORT: { test: RegExp; code: string }[] = [
  { test: /round\s*of\s*128/i, code: 'R128' },
  { test: /round\s*of\s*64/i, code: 'R64' },
  { test: /round\s*of\s*32/i, code: 'R32' },
  { test: /round\s*of\s*16/i, code: 'R16' },
  { test: /quarter/i, code: 'QF' },
  { test: /semi/i, code: 'SF' },
  { test: /final/i, code: 'F' },
]

function roundShort(roundLabel: string): string {
  for (const { test, code } of ROUND_SHORT) {
    if (test.test(roundLabel)) return code
  }
  return roundLabel.trim().slice(0, 3).toUpperCase()
}

/** Uppercase first three letters of a name (digits/spaces/punctuation stripped). */
function nameAbbrev(name: string): string {
  const letters = name.replace(/[^a-zA-Z]/g, '')
  return letters.slice(0, 3).toUpperCase()
}

/**
 * Tight code for a resolved team: FIFA tri-code by country, else a name-derived abbreviation.
 * Falls back to the upper-cased country code when nothing else is available.
 */
export function toTeamAbbrev(slot: BracketTeamSlot): string {
  const code = slot.countryCode != null ? slot.countryCode.toLowerCase() : ''
  const tri = COUNTRY_TRI_CODE[code]
  if (tri != null) return tri
  if (slot.name != null && slot.name !== '') return nameAbbrev(slot.name)
  return code.toUpperCase()
}

/**
 * Compact code for an undecided slot, derived from our own placeholder labels:
 *   "1st · Group A"               → "1A"
 *   "2nd · Group H"               → "2H"
 *   "3rd place"                   → "3rd"
 *   "Winner of Quarter-finals 2"  → "QF2"
 *   "Winner of Round of 16 3"     → "R16·3"
 *   "Loser of Semi-final 1"       → "L·SF1"
 *   "Winner Group A" / unknown    → initials / "TBD"
 */
export function compactPlaceholder(label: string | null | undefined): string {
  if (label == null || label.trim() === '') return 'TBD'
  const text = label.trim()

  const groupSeed = text.match(/^(\d)(?:st|nd|rd|th)?\s*[·•.]?\s*Group\s+([A-Za-z])/i)
  if (groupSeed != null) return `${groupSeed[1]}${groupSeed[2].toUpperCase()}`

  if (/^3rd\s*place/i.test(text)) return '3rd'

  const winnerOf = text.match(/^Winner\s+of\s+(.+?)\s+(\d+)$/i)
  if (winnerOf != null) {
    const short = roundShort(winnerOf[1])
    // A letter-only code (QF/SF/F) reads cleanly glued to the number; an R-prefixed code needs a
    // separator so "R16" + "3" isn't misread.
    return /^[A-Z]+$/.test(short) ? `${short}${winnerOf[2]}` : `${short}·${winnerOf[2]}`
  }

  const loserOfSemi = text.match(/^Loser\s+of\s+Semi[- ]?final\s+(\d+)$/i)
  if (loserOfSemi != null) return `L·SF${loserOfSemi[1]}`

  const winnerGroup = text.match(/Group\s+([A-Za-z])/i)
  if (winnerGroup != null) return winnerGroup[1].toUpperCase()

  if (/to\s+be\s+decided/i.test(text)) return 'TBD'

  // Last resort: initials of the first two words (keeps the box from overflowing).
  return text
    .split(/\s+/)
    .slice(0, 3)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 4)
}
